import React, { useState, useEffect, useRef } from 'react';
import { API_ENDPOINTS } from '../../config/apiRouting';
import './TableChartDisplay.css';

interface Table {
  tableId: string;
  restaurantId: string;
  tableNumber: string;
  tableType: string;
  position: { x: number; y: number };
  dimensions: { width: number; height: number };
  capacity: { default: number; maximum: number; current: number };
  areaId?: string;
  shape: 'square' | 'circle' | 'rectangle';
  rotation: number;
  isJoined: boolean;
  joinedWith: string[];
  status: 'available' | 'occupied' | 'reserved' | 'maintenance' | 'dirty';
  active: boolean;
  description?: string;
  currentParty?: {
    customerName: string;
    partySize: number;
    waitTime: number;
    seatedTime?: string;
  };
}

interface Area {
  areaId: string;
  restaurantId: string;
  name: string;
  color: string;
  description: string;
  isDefault: boolean;
  active: boolean;
}

interface TableChartDisplayProps {
  restaurantId: string;
  onTableClick?: (table: Table) => void;
  refreshInterval?: number; // in milliseconds, default 10000 (10 seconds)
  authToken?: string; // JWT token for API authentication
}

const TableChartDisplay: React.FC<TableChartDisplayProps> = ({
  restaurantId,
  onTableClick,
  refreshInterval = 10000,
  authToken
}) => {
  const [tables, setTables] = useState<Table[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const [canvasSize] = useState({ width: 1200, height: 800 });
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load table data
  const loadTables = async () => {
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      const response = await fetch(API_ENDPOINTS.restaurantTables(restaurantId), { headers });
      if (!response.ok) throw new Error('Failed to load tables');
      
      const data = await response.json();
      console.log('Tables response:', data);
      
      // Handle different response formats
      let tableList = [];
      if (Array.isArray(data)) {
        tableList = data;
      } else if (data.data && Array.isArray(data.data)) {
        tableList = data.data;
      } else if (data.tables && Array.isArray(data.tables)) {
        tableList = data.tables;
      } else if (data.Items && Array.isArray(data.Items)) {
        tableList = data.Items;
      }
      
      setTables(tableList);
    } catch (err) {
      console.error('Error loading tables:', err);
      setError('Failed to load table layout');
    }
  };

  // Load areas data
  const loadAreas = async () => {
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      const response = await fetch(API_ENDPOINTS.restaurantAreas(restaurantId), { headers });
      if (!response.ok) throw new Error('Failed to load areas');
      
      const data = await response.json();
      console.log('Areas response:', data);
      
      // Handle different response formats
      let areaList = [];
      if (Array.isArray(data)) {
        areaList = data;
      } else if (data.data && Array.isArray(data.data)) {
        areaList = data.data;
      } else if (data.areas && Array.isArray(data.areas)) {
        areaList = data.areas;
      } else if (data.Items && Array.isArray(data.Items)) {
        areaList = data.Items;
      }
      
      setAreas(areaList);
    } catch (err) {
      console.error('Error loading areas:', err);
      // Don't set error for areas - they're optional
    }
  };

  // Load all data
  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadTables(), loadAreas()]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load and setup refresh
  useEffect(() => {
    loadData();
    
    // Set up auto-refresh
    refreshIntervalRef.current = setInterval(() => {
      loadTables(); // Only refresh tables for status updates
    }, refreshInterval);
    
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [restaurantId, refreshInterval, authToken]);

  const getTableStatusColor = (status: string): string => {
    switch (status) {
      case 'available':
        return '#4CAF50';
      case 'occupied':
        return '#FF9800';
      case 'reserved':
        return '#2196F3';
      case 'dirty':
        return '#795548';
      case 'maintenance':
        return '#9E9E9E';
      default:
        return '#757575';
    }
  };

  const getAreaById = (areaId?: string): Area | undefined => {
    return areas.find(area => area.areaId === areaId);
  };

  const formatWaitTime = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const renderTable = (table: Table) => {
    const area = getAreaById(table.areaId);
    const fillColor = getTableStatusColor(table.status);
    const strokeColor = area?.color || '#333';
    
    const centerX = table.position.x + table.dimensions.width / 2;
    const centerY = table.position.y + table.dimensions.height / 2;

    return (
      <g
        key={table.tableId}
        transform={`rotate(${table.rotation}, ${centerX}, ${centerY})`}
        onClick={() => onTableClick?.(table)}
        className="table-group"
        style={{ cursor: onTableClick ? 'pointer' : 'default' }}
      >
        {/* Table Shape */}
        {table.shape === 'circle' ? (
          <circle
            cx={centerX}
            cy={centerY}
            r={table.dimensions.width / 2}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2"
            className="table-shape"
          />
        ) : (
          <rect
            x={table.position.x}
            y={table.position.y}
            width={table.dimensions.width}
            height={table.dimensions.height}
            rx={table.shape === 'square' ? 8 : 16}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth="2"
            className="table-shape"
          />
        )}
        
        {/* Table Number */}
        <text
          x={centerX}
          y={centerY - 10}
          textAnchor="middle"
          className="table-number"
          fill="white"
          fontSize="16"
          fontWeight="bold"
        >
          {table.tableNumber}
        </text>
        
        {/* Status Text */}
        <text
          x={centerX}
          y={centerY + 5}
          textAnchor="middle"
          className="table-status"
          fill="white"
          fontSize="12"
        >
          {table.status === 'occupied' && table.currentParty
            ? `${table.currentParty.partySize} guests`
            : table.status.toUpperCase()
          }
        </text>
        
        {/* Wait Time for occupied tables */}
        {table.status === 'occupied' && table.currentParty && (
          <text
            x={centerX}
            y={centerY + 20}
            textAnchor="middle"
            className="table-time"
            fill="white"
            fontSize="11"
          >
            {formatWaitTime(table.currentParty.waitTime)}
          </text>
        )}
      </g>
    );
  };

  if (loading) {
    return (
      <div className="table-chart-display loading">
        <div className="loading-spinner">Loading table layout...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="table-chart-display error">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="table-chart-display">
      <div className="chart-header">
        <h3>Table Layout</h3>
        <div className="status-legend">
          <div className="legend-item">
            <span className="legend-color available"></span>
            <span>Available</span>
          </div>
          <div className="legend-item">
            <span className="legend-color occupied"></span>
            <span>Occupied</span>
          </div>
          <div className="legend-item">
            <span className="legend-color reserved"></span>
            <span>Reserved</span>
          </div>
          <div className="legend-item">
            <span className="legend-color dirty"></span>
            <span>Needs Cleaning</span>
          </div>
          <div className="legend-item">
            <span className="legend-color maintenance"></span>
            <span>Maintenance</span>
          </div>
        </div>
      </div>
      
      <div className="chart-canvas" ref={canvasRef}>
        <svg
          width={canvasSize.width}
          height={canvasSize.height}
          viewBox={`0 0 ${canvasSize.width} ${canvasSize.height}`}
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Render Areas as background */}
          {areas.map(area => (
            <rect
              key={area.areaId}
              x="0"
              y="0"
              width={canvasSize.width}
              height={canvasSize.height}
              fill={area.color}
              fillOpacity="0.1"
              className="area-background"
            />
          ))}
          
          {/* Render Tables */}
          {tables.filter(table => table.active).map(renderTable)}
        </svg>
      </div>
      
      <div className="chart-footer">
        <div className="table-stats">
          <span>Available: {tables.filter(t => t.status === 'available').length}</span>
          <span>Occupied: {tables.filter(t => t.status === 'occupied').length}</span>
          <span>Total: {tables.filter(t => t.active).length}</span>
        </div>
        <div className="last-update">
          Auto-refresh every {refreshInterval / 1000}s
        </div>
      </div>
    </div>
  );
};

export default TableChartDisplay;